<i18n src="@/locales/models/exported_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_package.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_exp_package.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/process/harvest_export/new_harvested_batch.yml"
  lang="yaml"
></i18n>

<template>
  <div>
    <go-back-link :text="$t('labels.go_back_link')" :name="routeName('show_exported_batch')" :params="{
      organization_id: organization_id,
      harvest_process_id: harvest_process_id,
      exported_batch_id: exported_batch_id
    }" />
    <bread-crumbs :labels="[module_name, harvest_process.name]"></bread-crumbs>

    <alert-danger :errors_data="std_err" />

    <template v-if="load_general_data">
      <exported-batch-info :exported_batch="exported_batch" />

      <div class="mt-8">
        <div class="panel">
          <div class="panel-header">
            <h3 class="panel-header-title">
              {{ $t("labels.title") }}
            </h3>
          </div>

          <div class="panel-body">
            <div class="space-y-8 divide-y">
              <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_batch.batch_code") }}
                  </label>
                  <div class="mt-1">
                    <input type="text" v-model.trim="harvested_batch.batch_code"
                      :class="['input-text', isInvalid('batch_code')]" />
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_batch.registered_at") }} (DD/MM/YYYY)
                  </label>
                  <div class="mt-1">
                    <date-form v-model="harvested_batch.registered_at" :readonly="false" />
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_batch.producer") }}
                  </label>
                  <div class="mt-1">
                    <select-form :options="producers" label="full_label" v-model="harvested_batch.producer_id"
                      :is_invalid="isInvalidSelect('producer')" />
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_batch.crop_type") }}
                  </label>
                  <div class="mt-1" v-if="crop_types.length > 1">
                    <select-form :options="crop_types" v-model="harvested_batch.crop_type_id"
                      :is_invalid="isInvalidSelect('crop_type')" />
                  </div>
                  <dd class="content-data" v-else-if="crop_types.length == 1">
                    {{ crop_types[0].name }}
                  </dd>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_batch.total_weight") }}
                  </label>
                  <div class="flex mt-1">
                    <input type="text" v-model.number.trim="harvested_batch.total_weight"
                      :class="['input-text', isInvalid('total_weight')]" />
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_batch.harvest_team") }}
                  </label>
                  <div class="mt-1" v-if="harvest_teams.length > 1">
                    <select-form :options="harvest_teams" v-model="harvested_batch.harvest_team_id"
                      :is_invalid="isInvalidSelect('harvest_team')" />
                  </div>
                  <dd class="content-data" v-else-if="harvest_teams.length == 1">
                    {{ harvest_teams[0].name }}
                  </dd>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_batch.crop_field") }}
                  </label>
                  <div class="mt-1" v-if="crop_fields.length > 1">
                    <select-form :options="crop_fields" v-model="harvested_batch.crop_field_id" />
                  </div>
                  <dd class="content-data" v-else-if="crop_fields.length == 1">
                    {{ crop_fields[0].name }}
                  </dd>
                  <info-badge class="mt-2" v-if="crop_field?.observations" :label="crop_field?.observations"></info-badge>
                </div>
                <template v-for="(crop_har_package, index) in crop_har_packages" :key="crop_har_package.id">
                  <div class="sm:col-span-3">
                    <label class="label-form">
                      {{ $t("model.harvested_package.number") }} ({{ crop_har_package.name }})
                    </label>
                    <div class="flex mt-1">
                      <input type="text" v-model.number.trim="harvested_packages[index].number"
                        :class="['input-text', isInvalid('number')]" />
                    </div>
                  </div>
                </template>
              </div>
              <div class="py-4" v-for="(harvested_exp_package, index) in harvested_exp_packages" :key="index">
                <div class="flex justify-end" v-if="display_add_remove_buttons">
                  <button type="button" @click.prevent="removeHarvestedExpPackage(index)" class="btn-destroy-sm">
                    <i class="fa-solid fa-trash-can" />
                  </button>
                </div>
                <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div class="sm:col-span-3">
                    <label class="label-form">
                      {{ $t("model.harvested_exp_package.crop_exp_package") }}
                    </label>
                    <div class="mt-1" v-if="crop_exp_packages.length > 1">
                      <select-form :options="crop_exp_packages" v-model="harvested_exp_package.crop_package_id" />
                    </div>
                    <dd class="content-data" v-else-if="crop_exp_packages.length == 1">
                      {{ crop_exp_packages[0].name }}
                    </dd>
                  </div>
                  <div class="sm:col-span-3" v-if="crop_categories.length > 1">
                    <label class="label-form">
                      {{ $t("model.harvested_exp_package.crop_category") }}
                    </label>
                    <div class="mt-1" v-if="crop_categories.length > 1">
                      <select-form :options="crop_categories" v-model="harvested_exp_package.crop_category_id" />
                    </div>
                    <dd class="content-data" v-else-if="crop_categories.length == 1">
                      {{ crop_categories[0].name }}
                    </dd>
                  </div>
                  <div class="sm:col-span-3" v-if="crop_sizes.length > 1">
                    <label class="label-form">
                      {{ $t("model.harvested_exp_package.crop_size") }}
                    </label>
                    <div class="mt-1" v-if="crop_sizes.length > 1">
                      <select-form :options="crop_sizes" v-model="harvested_exp_package.crop_size_id" />
                    </div>
                    <dd class="content-data" v-else-if="crop_sizes.length == 1">
                      {{ crop_sizes[0].name }}
                    </dd>
                  </div>
                  <div class="sm:col-span-3">
                    <label class="label-form">
                      {{ $t("model.harvested_exp_package.number") }}
                    </label>
                    <div class="mt-1">
                      <input type="text" v-model.trim="harvested_exp_package.number"
                        :class="['input-text', isInvalid('number')]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-end mt-4" v-if="display_add_remove_buttons">
              <white-button @click.prevent="addHarvestedExpPackage()" :label="$t('labels.add_package_button')" />
            </div>
          </div>

          <div class="panel-footer">
            <div class="panel-footer-actions">
              <white-button @click.prevent="goToShowExportedBatch()" :label="$t('labels.cancel_button')" />
              <span class="space-actions"></span>
              <success-button v-if="!charging_submit" @click.prevent="create()" :label="$t('labels.save_button')" />
              <success-button-processing v-else :label="$t('labels.save_button')" />
            </div>
          </div>
        </div>
      </div>
    </template>
    <loading-message v-else />
  </div>
</template>

<script>
import moment from "moment";
import Requester from "@/views/organization/requesters/process/harvest_export/manage_harvested_batch.js";
import ExportedBatchInfo from "@/components/views/organization/process/harvest_export/ExportedBatchInfo.vue";

export default {
  name: "ORGProcessHarvestExportNewExportedBatch",
  components: {
    ExportedBatchInfo
  },
  data() {
    return {
      load_general_data: false,
      charging_submit: false,
      harvest_process: {},
      exported_batch: {},
      harvested_batch: {
        batch_code: "",
        total_weight: null,
        registered_at: null,
        crop_type_id: null,
        crop_variety_id: null,
        producer_id: null,
        harvest_team_id: null,
        crop_field_id: null
      },
      harvested_exp_package_default: {
        crop_exp_package_id: null,
        crop_category_id: null,
        crop_size_id: null,
        number: null
      },
      harvested_exp_packages: [],
      crop_types: [],
      crop_varieties: [],
      producers: [],
      crop_har_packages: [],
      harvested_packages: [],
      harvest_teams: [],
      process_lines: [],
      crop_fields: [],
      crop_sizes: [],
      crop_categories: [],
      crop_exp_packages: []
    };
  },
  props: {
    organization_id: { required: true },
    harvest_process_id: { required: true },
    exported_batch_id: { required: true }
  },
  created() {
    this.checkIfAuthenticated().then(() => {
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => {
        this.harvested_batch.registered_at = moment().format("YYYY-MM-DD");
        this.loadData();
      });
    });
  },
  computed: {
    harvested_exp_packages_params: function () {
      return this.harvested_exp_packages.filter(
        har_exp_package => Number(har_exp_package.number) > 0
      );
    },
    crop_field: function () {
      return this.crop_fields.find(
        crop_field => crop_field.id == this.harvested_batch.crop_field_id
      );
    },
    display_add_remove_buttons: function () {
      return (
        this.crop_exp_packages.length > 1 &&
        this.crop_categories.length > 1 &&
        this.crop_sizes.length > 1
      );
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchHarvestProcess();
      await this.fetchExportedBatch();
      await this.fetchCropTypes();
      await this.fetchProducers();
      await this.fetchCropHarPackages();
      await this.fetchHarvestTeams();
      await this.fetchCropCategories();
      await this.fetchCropSizes();
      await this.fetchCropExpPackages();
      this.addHarvestedExpPackage();
      this.load_general_data = true;
    },
    fetchHarvestProcess: async function () {
      const response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },
    fetchExportedBatch: async function () {
      const response = await Requester.fetchExportedBatch(
        this.organization_id,
        this.exported_batch_id
      );
      this.exported_batch = response.data;
    },
    fetchCropTypes: async function () {
      const response = await Requester.fetchCropTypes(
        this.organization_id,
        this.harvest_process_id
      );
      this.crop_types = response.data;
      this.harvested_batch.crop_type_id = this.hasOneItem(this.crop_types);
    },
    fetchHarvestTeams: async function () {
      const response = await Requester.fetchHarvestTeams(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_teams = response.data;
      this.harvest_teams.forEach(team => {
        team["name"] = team.harvest_team_code;
      });
    },
    fetchProducers: async function () {
      const response = await Requester.fetchProducers(this.organization_id);
      this.producers = response.data;
      this.producers.forEach(producer => {
        producer.full_label = `${producer.organization_producer_code} - ${producer.fullname}`;
      });
    },
    fetchCropHarPackages: async function () {
      const response = await Requester.fetchCropHarPackages(
        this.organization_id,
        this.harvest_process_id
      );
      this.crop_har_packages = response.data;
      this.harvested_packages = this.crop_har_packages.map(item => {
        return {
          id: null,
          crop_har_package_id: item.id,
          number: null
        };
      });
    },
    fetchCropFields: async function () {
      const response = await Requester.fetchCropFields(
        this.organization_id,
        this.harvest_process_id,
        this.harvested_batch.producer_id
      );
      this.crop_fields = response.data;
      this.harvested_batch.crop_field_id = this.hasOneItem(this.crop_fields);
    },
    fetchCropCategories: async function () {
      const response = await Requester.fetchCropCategories(
        this.organization_id,
        this.harvest_process_id
      );
      this.crop_categories = response.data;
      this.harvested_exp_package_default.crop_category_id = this.hasOneItem(
        this.crop_categories
      );
    },
    fetchCropSizes: async function () {
      const response = await Requester.fetchCropSizes(
        this.organization_id,
        this.harvest_process_id
      );
      this.crop_sizes = response.data;
      this.harvested_exp_package_default.crop_size_id = this.hasOneItem(
        this.crop_sizes
      );
    },
    fetchCropExpPackages: async function () {
      const response = await Requester.fetchCropExpPackages(
        this.organization_id,
        this.harvest_process_id
      );
      this.crop_exp_packages = response.data;
      this.harvested_exp_package_default.crop_exp_package_id = this.hasOneItem(
        this.crop_exp_packages
      );
    },
    create: async function () {
      this.charging_submit = true;
      try {
        let response = await Requester.create(
          this.organization_id,
          this.harvest_process_id,
          this.exported_batch_id,
          {
            ...this.harvested_batch,
            harvested_packages: this.harvested_packages.filter(
              har_package => Number(har_package.number) > 0
            ),
            harvested_exp_packages: this.harvested_exp_packages_params
          }
        );

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowExportedBatch();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.charging_submit = false;
    },
    addHarvestedExpPackage: function () {
      this.harvested_exp_packages.push({
        ...this.harvested_exp_package_default
      });
    },
    removeHarvestedExpPackage: function (index) {
      this.harvested_exp_packages.splice(index, 1);
      if (this.harvested_exp_packages.length == 0)
        this.addHarvestedExpPackage();
    },
    goToShowExportedBatch: function () {
      this.$router.push({
        name: this.routeName("show_exported_batch"),
        params: {
          organization_id: this.organization_id,
          harvest_process_id: this.harvest_process_id,
          exported_batch_id: this.exported_batch_id
        }
      });
    },
    hasOneItem: function (array) {
      return array.length == 1 ? array[0].id : null;
    }
  },
  watch: {
    harvested_packages: {
      handler: function () {
        this.harvested_packages.forEach(har_package => {
          if (isNaN(har_package.number)) har_package.number = "";
          if (har_package.number && !isNaN(har_package.number)) {
            har_package.number = Math.abs(Math.floor(har_package.number));
          }
        });
      },
      immediate: true,
      deep: true
    },
    "harvested_batch.producer_id": function (new_value) {
      this.harvested_batch.crop_field_id = null;
      this.harvested_batch.crop_variety_id = null;
      this.crop_fields = [];
      if (new_value) this.fetchCropFields();
    },
    "harvested_batch.crop_field_id": function (new_value) {
      this.harvested_batch.process_line_id = null;
      if (new_value) {
        this.harvested_batch.crop_variety_id = this.crop_field.crop_variety_id;
      }
    }
  }
};
</script>
